import { IGenericType } from '@library/common/generictype';
import { Accessorials, Attachment, BillingParties, BillingParty, Calculator, Reference, Risks, Status, Tradeshow } from '../base';
import { QuotationMode } from './quotation-modes';
import { QuotationType } from './quotation-types';
import { ModeMilestone } from './milestones/mode-milestone';

export interface IQuotation<T extends QuotationType, M extends QuotationMode> {
    readonly quoteType: T;
    readonly mode: M;

    fwq: string;

    billingParty: BillingParties;

    station: BillingParty;
    shipper: BillingParty;
    consignee: BillingParty;
    bill_to: BillingParty;

    movementType: IGenericType;

    serviceLevel: string;

    calculator: Calculator[];

    references?: Reference[];

    tradeshow?: Tradeshow[];

    risks?: Risks;

    accessorials?: Accessorials[];

    description?: string;
    specialInstructions?: string;

    statuses?: Status[];

    milestones?: IGenericType;

    attachments?: Attachment[];

    validToDate?: string;
    validToTime?: string;
    completedDateTime?: string;
    rejectionReason?: string;

    createdDate?: string;
    createdTime?: string;

    incoterm?: string;

    // pickup
    pdatefrom?: string;
    ptimefrom?: string;
    pdateto?: string;
    ptimeto?: string;

    // delivery
    ddatefrom?: string;
    ddateto?: string;
    dtimefrom?: string;
    dtimeto?: string;

    cost?: string;
    costCurrency?: string;

}

export class Quotation<T extends QuotationType, M extends QuotationMode> implements IQuotation<T, M> {
    readonly quoteType: T;
    readonly mode: M;

    fwq: string;
    
    billingParty: BillingParties;

    bill_to: BillingParty;
    station: BillingParty;
    consignee: BillingParty;
    shipper: BillingParty;
    
    serviceLevel: string;

    calculator: Calculator[];

    references?: Reference[];

    risks?: Risks;

    description?: string;
    accessorials?: Accessorials[];
    attachments?: Attachment[];
    milestones?: IGenericType;
    specialInstructions?: string;
    statuses?: Status[];
    tradeshow?: Tradeshow[];

    completedDateTime?: string;
    ddatefrom?: string;
    ddateto?: string;
    dtimefrom?: string;
    dtimeto?: string;
    incoterm?: string;
    pdatefrom?: string;
    pdateto?: string;
    ptimefrom?: string;
    ptimeto?: string;
    rejectionReason?: string;
    validToDate?: string;
    validToTime?: string;

    createdDate?: string;
    createdTime?: string;

    movementType: IGenericType;

    cost?: string;
    costCurrency?: string;


    constructor( quote?: IQuotation<T, M> ) {
        this.fwq = quote?.fwq;
        this.billingParty = quote?.billingParty;
        this.serviceLevel = quote?.serviceLevel;
        this.description = quote?.description;
        this.specialInstructions = quote?.specialInstructions;
        this.statuses = quote?.statuses;
        this.attachments = quote?.attachments;

        this.quoteType = quote?.quoteType;
        this.mode = quote?.mode;

        this.station = new BillingParty( quote?.station );
        this.shipper = new BillingParty( quote?.shipper );
        this.consignee = new BillingParty( quote?.consignee );
        this.bill_to = new BillingParty( quote?.bill_to );
        this.risks = new Risks( quote?.risks );
        this.milestones = new ModeMilestone( quote?.milestones );

        this.calculator = quote?.calculator?.map( c => new Calculator( c ) );
        this.references = quote?.references?.map( r => new Reference( r ) );
        this.tradeshow = quote?.tradeshow?.map( t => new Tradeshow( t ) );

        this.ddatefrom = quote?.ddatefrom;
        this.ddateto = quote?.ddateto;
        this.dtimefrom = quote?.dtimefrom;
        this.dtimeto = quote?.dtimeto;

        this.pdatefrom = quote?.pdatefrom;
        this.pdateto = quote?.pdateto;
        this.ptimefrom = quote?.ptimefrom;
        this.ptimeto = quote?.ptimeto;
        this.movementType = quote?.movementType || {};
        this.cost = quote?.cost || '0.000000';
        this.costCurrency = quote?.costCurrency;

        const currentDate = new Date();

        const date = quote?.createdDate || currentDate.getUTCFullYear() + '-' +
            (currentDate.getUTCMonth() + 1).toString().padStart(2, '0') + '-' + currentDate.getDate().toString().padStart(2, '0');
        const time = quote?.createdTime || currentDate.getUTCHours().toString().padStart(2, '0') + ':' + currentDate.getUTCMinutes().toString().padStart(2, '0') + ':' + currentDate.getUTCSeconds().toString().padStart(2, '0');

        if ( date.includes('-') ) {
            // date is still in UTC
            // do not allow non-UTC date time strings to be converted
            const datetime = date + 'T' + time + '.000Z';

            // this.datetime = (new Date( datetime )).getTime();

            this.createdDate = new Date( datetime ).toLocaleDateString().split('/').map((i) => i.padStart(2, '0')).join('/');
            this.createdTime = new Date( datetime ).toTimeString().split(' ')[0];
        }

        this.validToDate = quote?.validToDate;

        this.accessorials = quote?.accessorials;

        if(quote){
            const {
                quoteType,
                mode,
                fwq,
                billingParty,
                station,
                shipper,
                consignee,
                bill_to,
                serviceLevel,
                calculator,
                references,
                tradeshow,
                risks,
                accessorials,
                description,
                specialInstructions,
                statuses,
                attachments,
                milestones,
                movementType,
                createdDate,
                createdTime,
                ...others
            } = quote;

            for(const key in others){
                this[key] = others[key];
            }
        }
    }
}
